import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import Sparkline from "@/components/Sparkline";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Globe, Users, ArrowsClockwise, MapPin, Clock, TrendUp, Cursor, Timer, ArrowBendUpLeft, Megaphone, Trash } from "@phosphor-icons/react";

/**
 * v3.2.44 · Admin visitor analytics.
 *
 * Three panels:
 *   1. Tiles — 7d / 30d visits + uniques + distinct countries
 *   2. Country breakdown — ranked by visits (last 30d)
 *   3. Recent visits — last 50, masked IP + geo + path
 */
export default function AdminVisitorsPage() {
  const [summary, setSummary]     = useState(null);
  const [countries, setCountries] = useState([]);
  const [sources, setSources]     = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [daily, setDaily]         = useState([]);
  const [recent, setRecent]       = useState([]);
  const [busy, setBusy]           = useState(false);
  const [window, setWindow]       = useState(30);

  const load = async () => {
    setBusy(true);
    try {
      const [s, c, src, cam, d, r] = await Promise.all([
        api.get("/admin/visitors/summary"),
        api.get("/admin/visitors/by-country", { params: { days: window } }),
        api.get("/admin/visitors/by-source",  { params: { days: window } }),
        api.get("/admin/visitors/campaigns",  { params: { days: window } }),
        api.get("/admin/visitors/daily",      { params: { days: window } }),
        api.get("/admin/visitors/recent",     { params: { limit: 50 } }),
      ]);
      setSummary(s.data);
      setCountries(c.data || []);
      setSources(src.data || []);
      setCampaigns(cam.data || []);
      setDaily(d.data || []);
      setRecent(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [window]);

  const wipeAll = async () => {
    const total = summary?.total_all ?? 0;
    // NOTE: `window` is shadowed by a state var above — use globalThis.confirm.
    if (!globalThis.confirm(`Wipe ALL visitor data? This deletes ${total.toLocaleString()} visit row(s) and clears the IP-geo cache. This action cannot be undone.`)) {
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete("/admin/visitors");
      toast.success(`Wiped ${data.deleted?.page_visits ?? 0} visits · ${data.deleted?.ip_geo_cache ?? 0} geo rows`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Wipe failed");
    } finally { setBusy(false); }
  };

  const fmt = (n) => (n ?? 0).toLocaleString();
  const fmtDwell = (ms) => {
    const s = Math.round((ms || 0) / 1000);
    if (s <= 0) return "0s";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    return rs ? `${m}m ${rs}s` : `${m}m`;
  };
  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60)        return `${Math.floor(diff)}s ago`;
      if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
      if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
      if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
      return d.toISOString().slice(0, 10);
    } catch { return iso; }
  };

  const topCountry = countries[0];

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="admin-visitors" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Heading */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="label-tag">// admin · visitor analytics</div>
            <h1 className="mt-2 font-display text-4xl md:text-5xl font-black tracking-tighter"
                data-testid="visitors-heading">
              Visitors
            </h1>
            <p className="mt-2 text-sm text-gray-400 max-w-xl">
              Every landing hit and every authed page navigation is
              logged with country / region (via ipapi.co). Data
              self-prunes after 30 days.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={window}
              onChange={(e) => setWindow(Number(e.target.value))}
              className="bg-black/40 border border-white/10 text-white text-xs font-mono px-3 py-2 focus:border-[#00E5FF] outline-none"
              data-testid="visitors-window-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={load}
              disabled={busy}
              className="btn-ghost inline-flex items-center gap-2 text-xs py-2 px-3 disabled:opacity-50"
              data-testid="visitors-refresh-btn"
            >
              <ArrowsClockwise size={14} weight="bold" className={busy ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={wipeAll}
              disabled={busy || !summary?.total_all}
              title="Delete every page_visits row + clear the IP-geo cache"
              className="inline-flex items-center gap-2 text-xs py-2 px-3 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="visitors-wipe-btn"
            >
              <Trash size={14} weight="bold" /> Clear all
            </button>
          </div>
        </div>

        {/* Tiles */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="visitors-tiles">
          <Tile label="visits · 7d"     value={fmt(summary?.visits_7d)}   icon={Clock}  />
          <Tile label="uniques · 7d"    value={fmt(summary?.uniques_7d)}  icon={Users}  />
          <Tile label="visits · 30d"    value={fmt(summary?.visits_30d)}  icon={Clock}  />
          <Tile label="uniques · 30d"   value={fmt(summary?.uniques_30d)} icon={Users}  />
          <Tile label="countries · 30d" value={fmt(summary?.countries_30d)} icon={Globe} />
        </div>

        {/* v3.2.46 · Engagement tiles */}
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="visitors-engagement-tiles">
          <Tile label="engaged · 7d"      value={fmt(summary?.engaged_7d)}
                icon={Cursor}
                sub="visits with ≥1 second on page" />
          <Tile label="avg dwell · 7d"    value={fmtDwell(summary?.avg_dwell_ms_7d)}
                icon={Timer}
                sub="time on page (engaged rows only)" />
          <Tile label="avg clicks · 7d"   value={(summary?.avg_clicks_7d || 0).toFixed(1)}
                icon={Cursor}
                sub="user clicks per engaged pageview" />
          <Tile label="bounces · 7d"      value={fmt(summary?.bounces_7d)}
                icon={ArrowBendUpLeft}
                sub={`bounce rate · ${((summary?.bounce_rate_7d || 0) * 100).toFixed(0)}%  (dwell < 3s, no clicks)`} />
        </div>

        {topCountry && (
          <div className="mt-3 text-xs font-mono text-gray-500">
            Top country · <span className="text-[#00E5FF]">{topCountry.country}</span>
            {" · "}{fmt(topCountry.visits)} visits from {fmt(topCountry.uniques)} unique{topCountry.uniques === 1 ? "" : "s"}
          </div>
        )}

        {/* v3.2.45 · Daily trend sparkline */}
        <div className="mt-6 border border-white/10 bg-black/40 p-5" data-testid="visitors-trend-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="label-tag inline-flex items-center gap-1">
                <TrendUp size={12} weight="bold" /> // daily visits · last {window}d
              </div>
              <div className="mt-2 text-2xl font-display font-black text-white">
                {fmt(daily.reduce((s, r) => s + r.visits, 0))}
                <span className="ml-2 text-xs font-mono text-gray-500">total across the window</span>
              </div>
            </div>
            {daily.length > 0 && (
              <div className="text-right text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                <div>{daily[0].date}</div>
                <div className="mt-1 text-gray-600">→</div>
                <div>{daily[daily.length - 1].date}</div>
              </div>
            )}
          </div>
          <div className="mt-4">
            <Sparkline
              data={daily.map((r) => r.visits)}
              labels={daily.map((r) => r.date)}
              width={880}
              height={56}
            />
          </div>
        </div>

        {/* Two-column layout */}
        <div className="mt-6 grid md:grid-cols-5 gap-6">
          {/* By country + By source + Campaigns */}
          <div className="md:col-span-2 space-y-4">
            <div className="border border-white/10 bg-black/40 p-5"
                 data-testid="visitors-by-country">
              <div className="flex items-center justify-between">
                <div className="label-tag">// by country · last {window}d</div>
                <span className="text-[10px] font-mono text-gray-500">{countries.length} rows</span>
              </div>
              {countries.length === 0 ? (
                <div className="mt-4 text-xs text-gray-500 font-mono">No visits yet.</div>
              ) : (
                <ul className="mt-4 divide-y divide-white/5">
                  {countries.map((c) => (
                    <li key={(c.code || "xx") + c.country}
                        className="py-2 flex items-center justify-between text-sm gap-3"
                        data-testid={`country-row-${c.code || "xx"}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-8 text-center text-[10px] font-mono text-gray-500 uppercase">
                          {c.code || "??"}
                        </span>
                        <span className="text-white truncate">{c.country}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs font-mono text-gray-400 shrink-0">
                        <span title="unique visitors">
                          <Users size={11} className="inline mr-1 -mt-0.5" />
                          {fmt(c.uniques)}
                        </span>
                        <span className="text-white font-bold w-14 text-right">{fmt(c.visits)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* v3.2.45 · Marketing sources */}
            <div className="border border-white/10 bg-black/40 p-5"
                 data-testid="visitors-by-source">
              <div className="flex items-center justify-between">
                <div className="label-tag">// by source · last {window}d</div>
                <span className="text-[10px] font-mono text-gray-500">{sources.length} sources</span>
              </div>
              {sources.length === 0 ? (
                <div className="mt-4 text-xs text-gray-500 font-mono">No visits yet.</div>
              ) : (
                <ul className="mt-4 divide-y divide-white/5">
                  {sources.map((s) => (
                    <li key={s.source}
                        className="py-2 flex items-center justify-between text-sm gap-3"
                        data-testid={`source-row-${s.source}`}>
                      <SourceBadge source={s.source} />
                      <div className="flex items-center gap-4 text-xs font-mono text-gray-400 shrink-0">
                        {s.campaigns > 0 && (
                          <span title={`${s.campaigns} campaign${s.campaigns === 1 ? "" : "s"}`}
                                className="text-[#FFCC00]">
                            #{s.campaigns}
                          </span>
                        )}
                        <span title="unique visitors">
                          <Users size={11} className="inline mr-1 -mt-0.5" />
                          {fmt(s.uniques)}
                        </span>
                        <span className="text-white font-bold w-14 text-right">{fmt(s.visits)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* v3.2.47 · Active campaigns — clickable rows drill into
                 /admin/visitors/campaign/{name} for conversion metrics. */}
            <div className="border border-white/10 bg-black/40 p-5"
                 data-testid="visitors-campaigns">
              <div className="flex items-center justify-between">
                <div className="label-tag inline-flex items-center gap-1">
                  <Megaphone size={12} weight="bold" /> // campaigns · last {window}d
                </div>
                <span className="text-[10px] font-mono text-gray-500">{campaigns.length} active</span>
              </div>
              {campaigns.length === 0 ? (
                <div className="mt-4 text-xs text-gray-500 font-mono">
                  No UTM-tagged traffic yet. Add <span className="text-white">?utm_source=…</span> to
                  your outbound links.
                </div>
              ) : (
                <ul className="mt-4 divide-y divide-white/5">
                  {campaigns.slice(0, 8).map((c) => (
                    <li key={c.campaign}>
                      <Link
                        to={`/admin/visitors/campaign/${encodeURIComponent(c.campaign)}`}
                        className="py-2 flex items-center justify-between text-sm gap-3 hover:bg-white/[0.03] -mx-2 px-2 transition"
                        data-testid={`campaign-row-${c.campaign}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-white truncate">#{c.campaign}</div>
                          <div className="mt-0.5 text-[10px] font-mono text-gray-500 truncate">
                            {(c.sources || []).filter(Boolean).slice(0, 3).join(" · ") || "no source"}
                            {c.signups > 0 && (
                              <> · <span className="text-emerald-400">{c.signups} signup{c.signups === 1 ? "" : "s"}</span></>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-xs font-mono text-gray-400 shrink-0">
                          <span title="unique visitors">
                            <Users size={11} className="inline mr-1 -mt-0.5" />
                            {fmt(c.uniques)}
                          </span>
                          <span className="text-white font-bold w-14 text-right">{fmt(c.visits)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <div className="md:col-span-3 border border-white/10 bg-black/40 p-5"
               data-testid="visitors-recent">
            <div className="flex items-center justify-between">
              <div className="label-tag">// recent visits · newest first · last 50</div>
              <span className="text-[10px] font-mono text-gray-500">
                masked IPs · authed rows tagged
              </span>
            </div>
            {recent.length === 0 ? (
              <div className="mt-4 text-xs text-gray-500 font-mono">No visits yet.</div>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-gray-500 border-b border-white/10">
                    <tr>
                      <th className="pb-2 pr-3">When</th>
                      <th className="pb-2 pr-3">Path</th>
                      <th className="pb-2 pr-3">Source</th>
                      <th className="pb-2 pr-3">Engagement</th>
                      <th className="pb-2 pr-3">Location</th>
                      <th className="pb-2">Auth</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {/* v3.2.54 · Defensive sort — backend already returns
                          newest-first but a client-side sort protects against
                          any provider that reorders arrays or stale caches. */}
                    {[...recent]
                      .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""))
                      .map((r, i) => (
                      <tr key={i} data-testid={`visit-row-${i}`} className="align-top">
                        <td className="py-2 pr-3 font-mono text-gray-400 whitespace-nowrap">{fmtTime(r.ts)}</td>
                        <td className="py-2 pr-3 font-mono text-white truncate max-w-[180px]">
                          {r.path}
                        </td>
                        <td className="py-2 pr-3">
                          <SourceBadge source={r.source} tiny title={r.utm_campaign || r.referrer} />
                          {r.utm_campaign && (
                            <div className="mt-0.5 text-[9px] font-mono text-gray-500 truncate max-w-[130px]"
                                  title={`campaign · ${r.utm_campaign}`}>
                              #{r.utm_campaign}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 font-mono text-gray-400 whitespace-nowrap">
                          {r.dwell_ms > 0 ? (
                            <>
                              <span className="text-gray-200">{fmtDwell(r.dwell_ms)}</span>
                              <span className="text-gray-600"> · </span>
                              <span className="text-[#00E5FF]">{r.click_count}c</span>
                            </>
                          ) : (
                            <span className="text-gray-600">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-gray-300">
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={10} className="text-gray-500" />
                            {r.city ? `${r.city}, ` : ""}{r.country}
                          </span>
                        </td>
                        <td className="py-2 text-[10px] font-mono uppercase">
                          {r.auth ? (
                            <span className="text-emerald-400">signed in</span>
                          ) : (
                            <span className="text-gray-600">guest</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, sub }) {
  return (
    <div className="border border-white/10 bg-black/40 p-4"
         data-testid={`visitors-tile-${label.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
        <Icon size={16} weight="duotone" className="text-[#00E5FF]" />
      </div>
      <div className="mt-3 font-display text-3xl font-black text-white">{value}</div>
      {sub && (
        <div className="mt-1 font-mono text-[10px] text-gray-600 leading-tight">{sub}</div>
      )}
    </div>
  );
}

// v3.2.45 · Marketing source badge. Colour-codes the traffic bucket so
// the eye picks up the mix in a glance.
const SOURCE_COLORS = {
  direct:       { bg: "bg-white/5",         text: "text-gray-300",    dot: "bg-gray-400" },
  internal:     { bg: "bg-white/5",         text: "text-gray-400",    dot: "bg-gray-500" },
  google:       { bg: "bg-[#4285F4]/10",    text: "text-[#4285F4]",   dot: "bg-[#4285F4]" },
  bing:         { bg: "bg-teal-400/10",     text: "text-teal-300",    dot: "bg-teal-400" },
  duckduckgo:   { bg: "bg-orange-400/10",   text: "text-orange-300",  dot: "bg-orange-400" },
  yahoo:        { bg: "bg-purple-400/10",   text: "text-purple-300",  dot: "bg-purple-400" },
  baidu:        { bg: "bg-red-400/10",      text: "text-red-300",     dot: "bg-red-400" },
  twitter:      { bg: "bg-[#1DA1F2]/10",    text: "text-[#1DA1F2]",   dot: "bg-[#1DA1F2]" },
  facebook:     { bg: "bg-[#1877F2]/10",    text: "text-[#1877F2]",   dot: "bg-[#1877F2]" },
  linkedin:     { bg: "bg-[#0A66C2]/10",    text: "text-[#0A66C2]",   dot: "bg-[#0A66C2]" },
  reddit:       { bg: "bg-orange-500/10",   text: "text-orange-400",  dot: "bg-orange-500" },
  instagram:    { bg: "bg-pink-400/10",     text: "text-pink-300",    dot: "bg-pink-400" },
  youtube:      { bg: "bg-red-500/10",      text: "text-red-400",     dot: "bg-red-500" },
  telegram:     { bg: "bg-sky-400/10",      text: "text-sky-300",     dot: "bg-sky-400" },
  github:       { bg: "bg-white/5",         text: "text-white",       dot: "bg-white" },
  producthunt:  { bg: "bg-orange-400/10",   text: "text-orange-300",  dot: "bg-orange-400" },
  hackernews:   { bg: "bg-orange-500/10",   text: "text-orange-400",  dot: "bg-orange-500" },
  other:        { bg: "bg-[#FFCC00]/10",    text: "text-[#FFCC00]",   dot: "bg-[#FFCC00]" },
};

function SourceBadge({ source, tiny = false, title }) {
  const s = (source || "direct").toLowerCase();
  const c = SOURCE_COLORS[s] || SOURCE_COLORS.other;
  const size = tiny
    ? "px-1.5 py-0.5 text-[9px]"
    : "px-2 py-0.5 text-[10px]";
  return (
    <span
      className={`inline-flex items-center gap-1 font-mono uppercase tracking-wider ${c.bg} ${c.text} ${size}`}
      title={title || source}
      data-testid={`source-badge-${s}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {s}
    </span>
  );
}
